import { useContext } from 'react';
import { RichText } from 'prismic-reactjs';
import {
  format,
  formatDate,
  getEndOfFreeTrialDate,
} from '@monofront/business/utils';
import Modal from '@monofront/design-system/organisms/Modal';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import TimelineStepper, {
  IStep,
} from '@monofront/design-system/components/TimelineStepper';
import { LayoutContext } from '@context/LayoutContext';
import { PrismicRichTextInjecter } from '@monofront/slice-machine/helpers';
import useSubscriptionPrice from '@hooks/useSubscriptionPrice';

interface IModalSubscriptionReinsuranceProps {
  isOpen: boolean;
  closeModal: () => void;
}

const ModalSubscriptionReinsurance: React.FC<
  IModalSubscriptionReinsuranceProps
> = ({ isOpen, closeModal }) => {
  const { annualSubscriptionPrice } = useSubscriptionPrice();
  const { cartSharedSubscriptionTimeline } = useContext(LayoutContext);

  if (
    !cartSharedSubscriptionTimeline?.primary ||
    !cartSharedSubscriptionTimeline?.items?.length
  ) {
    return null;
  }

  const steps = cartSharedSubscriptionTimeline.items.reduce<IStep[]>(
    (stepList, { stepIcon, stepTitle, stepDescription }) => {
      if (!stepTitle.length || !stepDescription.length) {
        return stepList;
      }

      const step: IStep = {
        icon: stepIcon.url ? (
          <ResponsiveImage
            src={stepIcon.url}
            alt={stepIcon.alt}
            width={stepIcon.dimensions.width}
            height={stepIcon.dimensions.height}
          />
        ) : null,
        title: (
          <RichText
            htmlSerializer={PrismicRichTextInjecter({
              '%FREETRIALENDDATE%': formatDate(getEndOfFreeTrialDate()),
              '%SUBSCRIPTIONPRICE%': format(annualSubscriptionPrice),
            })}
            render={stepTitle}
          />
        ),
        description: (
          <RichText
            htmlSerializer={PrismicRichTextInjecter({
              '%FREETRIALENDDATE%': formatDate(getEndOfFreeTrialDate()),
              '%SUBSCRIPTIONPRICE%': format(annualSubscriptionPrice),
            })}
            render={stepDescription}
          />
        ),
      };

      return [...stepList, step];
    },
    [],
  );

  return (
    <Modal
      isOpen={isOpen}
      externalToggle={closeModal}
      UNSAFE_className="
        modal-subscription-reinsurance
        tw-max-w-[650px] desktop:tw-top-[10%]
      "
      hasPadding
    >
      <h2 className="tw-lf-title-sm tw-mb-8 tw-text-center">
        {cartSharedSubscriptionTimeline.primary.title}
      </h2>

      <div className="tw-mx-3 desktop:tw-mx-10">
        <TimelineStepper steps={steps} progress={1} />
      </div>

      <button
        onClick={closeModal}
        type="button"
        className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-mx-auto tw-mt-8"
        data-testid="ModalSubscriptionReinsurance-Button-close"
      >
        {cartSharedSubscriptionTimeline.primary.ctaConfirmation}
      </button>
    </Modal>
  );
};

export default ModalSubscriptionReinsurance;
